import React from 'react';
import { calculateCommissionBreakdown } from '../../lib/commission';

interface PaymentMethodSelectorProps {
    selectedMethod: 'wallet' | 'card';
    onMethodSelect: (method: 'wallet' | 'card') => void;
    price: number;
    userBalance: number;
    className?: string;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
    selectedMethod,
    onMethodSelect,
    price,
    userBalance,
    className = ""
}) => {
    const breakdown = calculateCommissionBreakdown(price);
    const canAffordWithWallet = userBalance >= breakdown.buyerPays;

    return (
        <div className={`space-y-6 ${className}`}>
            <div className="flex flex-col gap-3">
                <p className="text-sm font-black text-brand-clay dark:text-white uppercase tracking-widest">Ödeme Yöntemi</p>
                <div className="grid grid-cols-2 gap-4">
                    {/* Wallet Option */}
                    <button
                        onClick={() => onMethodSelect('wallet')}
                        className={`
                            relative h-24 rounded-3xl border-2 transition-all duration-300 flex flex-col items-center justify-center gap-1
                            ${selectedMethod === 'wallet'
                                ? 'border-brand-clay dark:border-white bg-brand-clay/5 dark:bg-white/10 text-brand-clay dark:text-white shadow-lg shadow-brand-clay/20 dark:shadow-none scale-[1.02]'
                                : 'border-gray-100 dark:border-white/5 hover:border-brand-clay/20 dark:hover:border-white/20 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-900'
                            }
                        `}
                    >
                        <span className="text-2xl">💼</span>
                        <span className="text-xs font-black uppercase tracking-widest">Cüzdan</span>
                        <span className={`text-[10px] font-bold ${canAffordWithWallet ? 'text-brand-clay/60 dark:text-white/40' : 'text-red-500'}`}>
                            ₺{userBalance.toLocaleString('tr-TR')}
                        </span>
                        {selectedMethod === 'wallet' && (
                            <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-brand-clay animate-pulse" />
                        )}
                    </button>

                    {/* Card Option */}
                    <button
                        onClick={() => onMethodSelect('card')}
                        className={`
                            relative h-24 rounded-3xl border-2 transition-all duration-300 flex flex-col items-center justify-center gap-1
                            ${selectedMethod === 'card'
                                ? 'border-brand-clay dark:border-white bg-brand-clay/5 dark:bg-white/10 text-brand-clay dark:text-white shadow-lg shadow-brand-clay/20 dark:shadow-none scale-[1.02]'
                                : 'border-gray-100 dark:border-white/5 hover:border-brand-clay/20 dark:hover:border-white/20 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-900'
                            }
                        `}
                    >
                        <span className="text-2xl">💳</span>
                        <span className="text-xs font-black uppercase tracking-widest">Kredi Kartı</span>
                        <span className="text-[10px] font-bold text-brand-clay/60 dark:text-white/40">Tek Çekim</span>
                        {selectedMethod === 'card' && (
                            <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-brand-clay animate-pulse" />
                        )}
                    </button>
                </div>
            </div>

            {/* Price Breakdown */}
            <div className="bg-white dark:bg-gray-900 rounded-3xl p-5 border border-gray-100 dark:border-white/5 space-y-3">
                <div className="flex justify-between items-center text-sm">
                    <span className="text-brand-clay/60 dark:text-gray-500 font-medium">Ürün Bedeli</span>
                    <span className="text-brand-clay dark:text-white font-bold">₺{price.toLocaleString('tr-TR')}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                    <span className="text-brand-clay/60 dark:text-gray-500 font-medium">Hizmet Bedeli</span>
                    <span className="text-brand-clay dark:text-white font-bold">₺{breakdown.buyerCommission.toLocaleString('tr-TR')}</span>
                </div>
                <div className="pt-3 border-t border-gray-100 dark:border-white/5 flex justify-between items-center">
                    <span className="text-brand-clay dark:text-white font-black uppercase tracking-widest text-xs">Toplam</span>
                    <span className="text-xl font-black text-brand-terracotta tracking-tighter">
                        ₺{breakdown.buyerPays.toLocaleString('tr-TR')}
                    </span>
                </div>
            </div>

            {/* Warnings */}
            {selectedMethod === 'wallet' && !canAffordWithWallet && (
                <div className="p-4 bg-red-50 dark:bg-red-500/10 rounded-2xl border border-red-100 dark:border-red-500/20 animate-in fade-in slide-in-from-top-1">
                    <div className="flex gap-3">
                        <span className="text-lg">⚠️</span>
                        <div>
                            <p className="text-xs font-black text-red-600 uppercase tracking-widest">Yetersiz Bakiye</p>
                            <p className="text-[11px] text-red-500 font-medium mt-0.5">
                                Cüzdanınızda yeterli bakiye bulunmuyor. Lütfen Kart ile Öde'yi seçin veya bakiye yükleyin.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentMethodSelector;
